import React from "react";
import { View, Text, StyleSheet, Platform } from "react-native";
import { COLORS } from "../../constants";
import { isMobileWeb } from "../../utils/helper";

/**
 * Returns a React component to render an overlay for highlighted events in an image carousel.
 *
 * @function
 * @param {object} imagesData - The data object containing event information.
 * @param {Array} imagesData.images - Array of image sources or identifiers.
 * @param {Array} imagesData.name - Array of event names.
 * @param {Array} imagesData.startDate - Array of event start dates.
 * @param {Array} imagesData.occurrence_id - Array of unique keys for sorting or identifying events.
 * @returns {function} OverlayComponent - A React component that renders the overlay for a given event item.
 */
export const HighlightedEventsOverlay = (imagesData) => {
  const OverlayComponent = (index) => {
    return (
      <View style={styles.overlayContainer}>
        <View style={styles.overlay}>
          <Text style={styles.textTitle}>{imagesData.name[index]}</Text>
          <Text style={styles.textDetails}>{imagesData.startDate[index]}</Text>
        </View>
      </View>
    );
  };

  return OverlayComponent;
};

/**
 * Returns a handler function for overlay press events that navigates to the "DetailsView" screen
 * with the corresponding event key from the highlighted events data.
 *
 * @param {object} navigation - The navigation object used to perform navigation actions.
 * @param {object} highlightedEventsData - The data object containing highlighted events, including an `occurrence_id` array.
 * @returns {(item: any, index: number) => void} - A function that takes an item and its index, then navigates to the details view for the selected event.
 */
export const onPressOverlay = (navigation, highlightedEventsData) => (item, index) => {
  const eventKey = highlightedEventsData.occurrence_id[index];
  navigation.navigate("DetailsView", { eventKey });
};

const styles = StyleSheet.create({
  overlayContainer: {
    flex: 1,
    position: "absolute",
    bottom: 0,
    paddingHorizontal: Platform.OS === "web" && !isMobileWeb() ? 0 : 35,
    width: Platform.OS === "web" && !isMobileWeb() ? "auto" : "100%",
  },
  overlay: {
    zIndex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    padding: 8,
  },
  textTitle: {
    color: COLORS.textLight,
    fontSize: Platform.OS === "web" && !isMobileWeb() ? 21 : 16,
  },
  textDetails: {
    color: COLORS.textLight,
    fontSize: Platform.OS === "web" && !isMobileWeb() ? 15 : 12,
    paddingTop: 6,
  },
});
